/**
 * リンクで確認の画面の日本語（en.confirmAction.ts と同じキーを全部持つ。漏れは typecheck で落ちる）。表記の決め事は ja.ts の冒頭と同じ。
 * ボタンの名前は MCP がエージェントに言わせる名前と同じ（apps/mcp/src/pendingActions.ts の APPROVE_LABEL の ja）
 */
import type { enConfirmAction } from './en.confirmAction';
import type { PluralMsg } from './index';

type ConfirmActionMessages = { [K in keyof typeof enConfirmAction]: (typeof enConfirmAction)[K] extends string ? string : string | PluralMsg };

export const jaConfirmAction: ConfirmActionMessages = {
  'confirmAction.lead': 'AI エージェントが、本番（利用者が使っている環境）でこの変更をしようとしています。まだ何も変わっていません。',
  'confirmAction.signedInAs': '{email} でログイン中',
  'confirmAction.notYou': '（別のアカウントに切り替える）',
  'confirmAction.what': '何が起きるか',
  'confirmAction.project': 'プロジェクト',
  'confirmAction.projectValue': '{name}（{slug}）',
  'confirmAction.projectUnknown': '不明なプロジェクト',
  'confirmAction.environment': '環境',
  'confirmAction.production': '本番（利用者が使っている環境）',
  'confirmAction.testApp': 'テスト用のアプリ（development）',
  'confirmAction.leadTest': 'AI エージェントが、テスト用のアプリ（公開中のアプリではありません）でこの変更をしようとしています。まだ何も変わっていません。',
  'confirmAction.client': '頼んだエージェント',
  'confirmAction.clientValue': '{name}（エージェントが名乗った名前。{product} は確かめられません）',
  'confirmAction.unnamed': 'AI エージェント',
  'confirmAction.expires': 'このリンクは {time} まで使えます。',
  'confirmAction.clientCli': '端末の {product} CLI（AI エージェントが動かしているかもしれません）',
  'confirmAction.cancelNote': '心当たりが無ければ「やめる」を押してください。何も変わらず、エージェントもこの操作を送れなくなります。',
  'confirmAction.howItWorks': '{product} がエージェントの代わりに実行するのではありません。許可すると、エージェントが同じ操作を数分のうちに（長くて 10 分）1 回だけ送れるようになります。',

  'confirmAction.leadBundle': 'AI エージェントが、本番（利用者がいる環境）で次の {count} 件の変更をこの順に頼んでいます。1 回の承認で全部を許可します。まだ何も起きていません。',
  'confirmAction.whatBundle': '何が起きるか（{count} 件・この順に）',
  'confirmAction.cancelNoteBundle': '心当たりの無いものが 1 つでもあれば「やめる」を押してください。何も変わらず、エージェントはどれも送れません。',
  'confirmAction.howItWorksBundle': '{product} がエージェントの代わりに実行するのではありません。承認すると、エージェントがこれらの要求をこの順に 1 回ずつ（数分のうちに・長くて 10 分）送れるようになります。',
  'confirmAction.bundleChanged': 'このページを開いたあとに、AI からのお願いが増えました。もう一度確かめてから押してください。',
  'confirmAction.rowStatus.pending': '待っています',
  'confirmAction.rowStatus.approved': '承認済み',
  'confirmAction.rowStatus.denied': '取り消し',
  'confirmAction.rowStatus.used': '完了',
  'confirmAction.rowStatus.expired': '期限切れ',
  'confirmAction.rowStatus.stale': '対象が変わった',
  'confirmAction.rowStatus.revoked': '接続が外された',

  'confirmAction.what.hosting_activate': 'バージョン {version} を本番に公開します。利用者にすぐ届きます。',
  'confirmAction.what.hosting_domain_add': '本番のアプリを {hostname}（独自ドメイン）で配信します。あなたの別のアプリがこのドメインをすでに使っている場合を除き、DNS のレコードを置く必要があります。',
  'confirmAction.what.hosting_domain_remove': '本番のアプリを独自ドメイン {hostname} から外します。そこでアプリを使っている利用者と、投稿したリンクはすぐに使えなくなります。同じドメインにあなたの他のアプリがあればそれらはそのまま使え、なければドメインごと外れます。',
  'confirmAction.what.functions_domain_add': '本番の Functions に独自ドメイン {hostname} を足します。DNS の 2 行を置くと配信が始まります。',
  'confirmAction.what.functions_domain_remove': '本番の Functions から独自ドメイン {hostname} を外します。そのドメインでの配信はすぐに止まり、他の人が登録できるようになります。',
  'confirmAction.what.config_push': '新しい設定を本番に反映します。利用者にすぐ効きます。',
  'confirmAction.what.config_push_invisible': '本番に設定を反映します（利用者に見える変化はありません）。',
  'confirmAction.what.config_push_hosting_on': '本番で公開（hosting）を ON にして、設定を反映します。',
  'confirmAction.what.config_push_on': '本番で {features} を ON にして、設定を反映します。利用者にすぐ効きます。',
  'confirmAction.what.players_ban': '利用者 {playerId} を BAN します。ログアウトされ、アプリを使えなくなります。',
  'confirmAction.what.players_unban': '利用者 {playerId} の BAN を解きます。',
  'confirmAction.what.appeals_accept': 'BAN への異議申し立て {appealId} を受け入れ、BAN を解きます。',
  'confirmAction.what.appeals_reject': 'BAN への異議申し立て {appealId} を退けます。BAN は続きます。',
  'confirmAction.what.functions_enable': '本番のサーバー関数を有効にします。使った分は前払い残高から原価で引かれます。',
  'confirmAction.what.functions_disable': '本番のサーバー関数を停止します。有効に戻すまで、サーバー関数への呼び出しは失敗します。',
  'confirmAction.what.functions_delete': '本番のサーバー関数のコードを削除します（データは残ります）。',
  'confirmAction.what.functions_purge': '本番のサーバー関数と、そのデータ（データベース・KV・ファイル・キュー）をすべて削除します。元に戻せません。',
  'confirmAction.what.functions_delete_invalid': '本番のサーバー関数を削除します。ただし指定が不正なため、許可しても {product} が断ります。',
  'confirmAction.what.functions_deploy': '新しいサーバーのコードを本番に反映します。利用者にすぐ届きます。',
  'confirmAction.what.functions_secret_put': '本番のサーバー関数の秘密 {name} を保存します（値はここには出しません）。',
  'confirmAction.what.functions_secret_delete': '本番のサーバー関数の秘密 {name} を削除します。',
  'confirmAction.what.functions_db_query': '本番のデータベース {binding} でこの SQL を実行します。',
  'confirmAction.what.functions_db_migrate': '本番のデータベース {binding} に migration を {count} 件適用します。',
  'confirmAction.what.mail_schedule': 'メール {subject} を利用者に予約で送ります。',
  'confirmAction.what.mail_repeat': 'メール {subject} を利用者にくり返し送る予約をします。',
  'confirmAction.what.mail_send': 'メール {subject} を今すぐ利用者に送ります。送ったメールは取り消せません。',
  'confirmAction.what.unknown': '本番で {method} {path} を実行します。',
  // テスト用（development）の確認（2026-10-08 core/dev-confirm-links）。「本番」と言わない
  'confirmAction.what.functions_delete_test': 'テスト用のアプリのサーバー関数のコードを削除します（データは残ります）。公開中のアプリは変わりません。',
  'confirmAction.what.functions_purge_test': 'テスト用のアプリのサーバー関数と、そのデータ（データベース・KV・ファイル・キュー）をすべて削除します。元に戻せません。公開中のアプリは変わりません。',
  'confirmAction.what.functions_delete_invalid_test': 'テスト用のアプリのサーバー関数を削除します。ただし指定が不正なため、許可しても {product} が断ります。',
  'confirmAction.what.functions_domain_remove_test': 'テスト用のアプリのサーバー関数から独自ドメイン {hostname} を外します。そのドメインでの配信はすぐに止まり、他の人が登録できるようになります。公開中のアプリは変わりません。',
  'confirmAction.what.unknown_test': 'テスト用のアプリで {method} {path} を実行します。',

  'confirmAction.extra.featuresOn': 'ON にする機能',
  'confirmAction.warning.stopServing': '公開中のサイトを止めます: {features} を OFF にし、もう一度 ON にするまで、サイトやシェアのリンクを開いた人には何も表示されません。',
  'confirmAction.extra.featuresOff': 'OFF にする機能',
  'confirmAction.extra.changedSections': '変わる項目',
  'confirmAction.extra.reason': '理由',
  'confirmAction.extra.until': '期限',
  'confirmAction.extra.response': '利用者に見える返信',
  'confirmAction.extra.mainModule': 'メインのモジュール',
  'confirmAction.extra.moduleCount': 'モジュールの数',
  'confirmAction.extra.bundleBytes': '大きさ（バイト）',
  'confirmAction.extra.varNames': '変数',
  'confirmAction.extra.crons': '定期実行（cron）',
  'confirmAction.extra.message': 'メモ',
  'confirmAction.extra.sql': 'SQL',
  'confirmAction.extra.sendAt': '送る時刻',
  'confirmAction.extra.recurrence': 'くり返し',
  'confirmAction.extra.unexpectedQuery': '追加の指定',
  // 2026-10-25 security/prompt-lava-fixes の D
  'confirmAction.extra.addedFiles': '追加するファイル',
  'confirmAction.extra.changedFiles': '変更するファイル',
  'confirmAction.extra.removedFiles': '削除するファイル',
  'confirmAction.extra.outboundHosts': 'コードに書かれた外のアドレス',

  'confirmAction.button.publish': '公開する',
  'confirmAction.button.domainAdd': '追加する',
  'confirmAction.button.domainRemove': '外す',
  'confirmAction.button.configPush': '変更する',
  'confirmAction.button.ban': '利用停止にする',
  'confirmAction.button.unban': '解除する',
  'confirmAction.button.appeal': '断る',
  'confirmAction.button.fnEnable': '動かす',
  'confirmAction.button.fnDisable': '止める',
  'confirmAction.button.fnDelete': '削除する',
  'confirmAction.button.fnDeploy': '更新する',
  'confirmAction.button.secretPut': '保存する',
  'confirmAction.button.secretDelete': '削除する',
  'confirmAction.button.dbQuery': '書き換える',
  'confirmAction.button.dbMigrate': '変更する',
  'confirmAction.button.mailSchedule': '予約する',
  'confirmAction.button.mailSend': '送信する',
  'confirmAction.button.allow': '許可する',
  'confirmAction.button.configStop': '公開をやめる',
  'confirmAction.button.configAllowPublish': '許可する',
  'confirmAction.button.configTurnOn': '追加する',
  'confirmAction.button.appealAccept': '受け入れる',
  'confirmAction.button.dbQueryRead': '実行する',
  'confirmAction.button.dbQueryDelete': '消す',
  'confirmAction.button.dbQueryInsert': '追加する',
  'confirmAction.button.dbQuerySchema': '変更する',

  'confirmAction.status.approved': '許可しました。AI エージェントが 1 回だけ送れるようになりました。',
  'confirmAction.status.approvedBundle': '許可しました。AI エージェントがこの {count} 件の変更を順に 1 回ずつ送れるようになりました。',
  'confirmAction.next.chat': '1 分たっても AI が「済んだ」と言わないときは、チャットに戻って『押した』と送ってください。チャットのアプリによっては、メッセージを送るまで続きが進みません。送られると、この画面は「済んだ」に変わります。',
  'confirmAction.next.cli': '{product} の CLI が自分で続けて送ります。この画面は閉じてかまいません。',
  'confirmAction.status.denied': '取り消しました。何も変わっていません。エージェントもこの操作を送れません。',
  'confirmAction.status.used': 'この操作はもう実行されました。ここですることはありません。',
  'confirmAction.status.expired': 'このリンクは期限が切れたか、使えないリンクです。何も変わっていません。まだ必要なら、AI エージェントとのチャットに戻って『もう一度』と送ってください。新しいリンクが届きます（20 分使えます）。',
  'confirmAction.status.stale': 'このリンクを作った後に対象が変わったため（公開中のバージョンや設定など）、もう許可できません。何も変わっていません。AI エージェントにもう一度頼んで、新しいリンクを受け取ってください。',
  'confirmAction.status.revoked': 'AI エージェントの {product} への接続が解除されたため、この操作はもうできません。何も変わっていません。',
  'confirmAction.wrongAccount': 'このリンクは別の {product} アカウント（{owner}）のものです。いまは {email} でログインしています。ログアウトして、そのアカウントでログインしてから、もう一度リンクを開いてください。何も変わっていません。',
  'confirmAction.switchAccount': 'ログアウトしてアカウントを切り替える',
  'confirmAction.invalidLink': 'この確認のリンクは使えません。何も変わっていません。',
  'confirmAction.notAvailable': 'この {product} のサーバーは、AI エージェントの変更をリンクで確認する仕組みにまだ対応していません。何も変わっていません。ダッシュボードで変更してください。',
  'confirmAction.emailUnverified': '先にメールアドレス（{email}）を確認してください。届いたメールのリンクを開いてください。「やめる」は押せます。'
};
